import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Paper,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

import {
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';
import azureService from '../services/azure.service';

interface Case {
  id: number;
  case_name: string;
  case_type: string;
  service_type_id: number;
  service_type?: { name: string; short_code: string };
  status: string;
  creator?: { name: string; email: string };
  firm?: { name: string };
  assigned_expert?: {
    id: number;
    name: string;
    email: string;
  };
  invoice_status?: 'paid' | 'unpaid';
}

interface AzureSlot {
  start: string;
  end: string;
  start_time: string;
  end_time: string;
  date: string;
  formatted_time: string;
  available_for?: string[];
}

const SlotSelection: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [azureSlots, setAzureSlots] = useState<AzureSlot[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<AzureSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDateRangeDialog, setShowDateRangeDialog] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() + 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0], // Tomorrow
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0], // 7 days from now
    duration_minutes: 180,
  });
  const [azureAuthenticated, setAzureAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [calendarUsers, setCalendarUsers] = useState<string[]>([]);
  const [availabilityMode, setAvailabilityMode] = useState<'any' | 'all'>('any');
  const [usesConfiguredCalendars, setUsesConfiguredCalendars] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCaseDetails();
      checkAzureAuth();
    }
  }, [id]);

  const checkAzureAuth = async () => {
    setCheckingAuth(true);
    try {
      console.log('Checking Azure authentication status...');
      const authStatus = await azureService.isAuthenticated();
      console.log('Azure auth status:', authStatus);

      const wasAuthenticated = azureAuthenticated;
      const configured = authStatus.calendar_source === 'configured_users';
      setUsesConfiguredCalendars(configured);
      setCalendarUsers(authStatus.calendar_users ?? []);
      setAvailabilityMode(authStatus.availability_mode === 'all' ? 'all' : 'any');
      setAzureAuthenticated(configured || authStatus.authenticated);

      // If we just became authenticated, automatically fetch slots
      if (!wasAuthenticated && authStatus.authenticated) {
        console.log(
          'Azure authentication detected, fetching slots automatically...'
        );
        setTimeout(() => fetchAzureCalendarSlots(), 1000); // Small delay to ensure state is updated
      }
    } catch (error) {
      console.error('Azure auth check error:', error);
      setAzureAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleAzureAuth = async () => {
    try {
      await azureService.initiateAuth();
      await checkAzureAuth();
      toast.success('Azure authentication successful!');
    } catch (error: any) {
      toast.error(error.message || 'Azure authentication failed');
    }
  };

  const fetchCaseDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/cases/${id}`);
      setCaseData(res.data);

      // Check access permissions for expert users
      const isAdmin = hasRole('admin');
      const isExpertUser = hasRole('expert_user');

      if (isExpertUser && !isAdmin) {
        // Expert users can only access if they are assigned to the case
        if (res.data.assigned_expert?.id !== user?.id) {
          setError(
            'You are not authorized to access slot management for this case. Only the assigned expert can access this page.'
          );
          return;
        }
      }
    } catch (err: any) {
      setError('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAzureCalendarSlots = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        duration_minutes: dateRange.duration_minutes.toString(),
      });
      const res = await api.get(`/cases/${id}/azure-calendar-slots?${params}`);
      // Convert object to array if needed
      const slotsObj = res.data.available_slots;
      const slotsArray = Array.isArray(slotsObj)
        ? slotsObj
        : slotsObj
          ? Object.values(slotsObj)
          : [];
      setAzureSlots(slotsArray);
      if (Array.isArray(res.data.calendar_users)) {
        setCalendarUsers(res.data.calendar_users);
      }
      if (res.data.calendar_source === 'configured_users') {
        setUsesConfiguredCalendars(true);
      }
      if (res.data.availability_mode === 'all' || res.data.availability_mode === 'any') {
        setAvailabilityMode(res.data.availability_mode);
      }
      setSelectedSlots([]); // Reset selections
      toast.success(`Retrieved ${slotsArray.length} available slots`);
    } catch (err: any) {
      setAzureSlots([]);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to retrieve Azure Calendar slots'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelection = (slot: AzureSlot, checked: boolean) => {
    if (checked) {
      setSelectedSlots(prev => [...prev, slot]);
    } else {
      setSelectedSlots(prev => prev.filter(s => s.start !== slot.start));
    }
  };

  const handleCreateSlots = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one slot');
      return;
    }

    if (!caseData?.assigned_expert?.id) {
      toast.error('No expert is assigned to this case');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/cases/${id}/create-slots-from-azure`, {
        selected_slots: selectedSlots,
        expert_id: caseData.assigned_expert.id,
      });

      toast.success(
        `Created ${selectedSlots.length} meeting slots with expert and sent email to firm admin successfully`
      );
      setSelectedSlots([]);
      setAzureSlots([]);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          'Failed to create meeting slots and send email'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Defensive groupSlotsByDate
  const groupSlotsByDate = (slots: AzureSlot[] | undefined | null) => {
    const grouped: { [key: string]: AzureSlot[] } = {};
    if (!Array.isArray(slots)) return grouped;
    slots.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });
    return grouped;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading && !caseData) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button onClick={() => navigate('/admin/cases')} sx={{ mb: 2 }}>
          Back to Cases
        </Button>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!caseData) {
    return (
      <Box>
        <Alert severity='warning'>Case not found</Alert>
      </Box>
    );
  }

  // When calling groupSlotsByDate, always pass an array
  const groupedSlots = groupSlotsByDate(azureSlots);
  console.log(groupedSlots);
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button onClick={() => navigate('/admin/cases')} sx={{ mr: 2 }}>
          Back to Cases
        </Button>
        <Typography variant='h4' sx={{ flexGrow: 1 }}>
          Azure Calendar Slot Selection
        </Typography>
        <Button
          variant='outlined'
          startIcon={<RefreshIcon />}
          onClick={() => setShowDateRangeDialog(true)}
          sx={{ mr: 2 }}
        >
          Change Date Range
        </Button>
        <Button
          variant='contained'
          startIcon={<CalendarIcon />}
          onClick={fetchAzureCalendarSlots}
          disabled={loading || (!azureAuthenticated && !usesConfiguredCalendars)}
        >
          {loading ? 'Loading...' : 'Get Azure Calendar Slots'}
        </Button>
      </Box>

      {/* Azure Authentication Status */}
      {!checkingAuth && (
        <Alert
          severity={azureAuthenticated ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          action={
            <Box>
              {!azureAuthenticated && !usesConfiguredCalendars && (
                <Button
                  color='inherit'
                  size='small'
                  onClick={handleAzureAuth}
                  sx={{ mr: 1 }}
                >
                  Connect Azure
                </Button>
              )}
              <Button color='inherit' size='small' onClick={checkAzureAuth}>
                Refresh Status
              </Button>
            </Box>
          }
        >
          {usesConfiguredCalendars
            ? `Reading ${calendarUsers.join(', ') || 'configured calendars'}. ${
                availabilityMode === 'all'
                  ? 'A time is shown only when every one of these calendars is free.'
                  : 'A time is shown when at least one of these calendars is free.'
              }`
            : azureAuthenticated
              ? 'Connected to Azure Calendar - You can fetch your calendar slots'
              : 'Connect to Azure Calendar to fetch your available time slots'}
        </Alert>
      )}

      {/* Case Info */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title='Case Information'
          subheader={`Case: ${caseData.case_name} (${caseData.service_type?.short_code || 'N/A'})`}
        />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='body2' color='text.secondary'>
                <strong>Case Type:</strong> {caseData.case_type}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Service Type:</strong>{' '}
                {caseData.service_type?.name || 'N/A'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Assigned Expert:</strong>{' '}
                {caseData.assigned_expert?.name || 'No expert assigned'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' color='text.secondary'>
                <strong>Firm Admin:</strong> {caseData.creator?.name || 'N/A'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Firm:</strong> {caseData.firm?.name || 'N/A'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Invoice Status:</strong>{' '}
                {caseData.invoice_status === 'paid' ? 'Paid' : 'Unpaid'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Selected Slots Summary */}
      {selectedSlots.length > 0 && (
        <Card
          sx={{
            mb: 3,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <CardContent>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <Typography variant='h6'>
                Selected Slots: {selectedSlots.length}
              </Typography>
              <Box>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<CheckIcon />}
                  onClick={handleCreateSlots}
                  disabled={submitting}
                >
                  {submitting
                    ? 'Creating & Sending...'
                    : 'Create Slots & Send to Firm Admin'}
                </Button>
              </Box>
            </Box>
            {caseData?.assigned_expert && (
              <Typography variant='body2' sx={{ mt: 1, opacity: 0.9 }}>
                Slots will be created for:{' '}
                <strong>{caseData.assigned_expert.name}</strong>
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Slots */}
      {azureSlots.length > 0 ? (
        <Card>
          <CardHeader
            title='Available Slots from Azure Calendar'
            subheader={
              usesConfiguredCalendars
                ? `Found ${azureSlots.length} slots from ${calendarUsers.join(', ')} (EDT)`
                : `Found ${azureSlots.length} available slots (All times in EDT - Eastern Daylight Time)`
            }
          />
          <CardContent>
            {Object.entries(groupedSlots).map(([date, slots]) => (
              <Box key={date} sx={{ mb: 3 }}>
                <Typography variant='h6' sx={{ mb: 2, color: 'primary.main' }}>
                  {formatDate(date)}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                      md: '1fr 1fr 1fr',
                    },
                    gap: 2,
                  }}
                >
                  {slots.map(slot => (
                    <Paper
                      key={slot.start}
                      elevation={
                        selectedSlots.some(s => s.start === slot.start) ? 8 : 2
                      }
                      sx={{
                        p: 2,
                        border: selectedSlots.some(s => s.start === slot.start)
                          ? 2
                          : 1,
                        borderColor: selectedSlots.some(
                          s => s.start === slot.start
                        )
                          ? 'primary.main'
                          : 'divider',
                        cursor: 'pointer',
                        '&:hover': { elevation: 4 },
                      }}
                      onClick={() =>
                        handleSlotSelection(
                          slot,
                          !selectedSlots.some(s => s.start === slot.start)
                        )
                      }
                    >
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Box>
                          <Typography variant='body1' fontWeight='bold'>
                            {slot.formatted_time}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {slot.start_time} - {slot.end_time} EDT
                          </Typography>
                          {slot.available_for && slot.available_for.length > 0 && (
                            <Typography variant='caption' color='text.secondary'>
                              Free: {slot.available_for.join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Checkbox
                          checked={selectedSlots.some(
                            s => s.start === slot.start
                          )}
                          color='primary'
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box textAlign='center' py={4}>
              <CalendarIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                No Azure Calendar Slots Retrieved
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {usesConfiguredCalendars
                  ? 'Click "Get Azure Calendar Slots" to retrieve open times from the configured expert calendars.'
                  : 'Click "Get Azure Calendar Slots" to retrieve available time slots from your Azure Calendar.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Date Range Dialog */}
      <Dialog
        open={showDateRangeDialog}
        onClose={() => setShowDateRangeDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Select Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label='Start Date'
              type='date'
              value={dateRange.start_date}
              onChange={e =>
                setDateRange(prev => ({ ...prev, start_date: e.target.value }))
              }
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label='End Date'
              type='date'
              value={dateRange.end_date}
              onChange={e =>
                setDateRange(prev => ({ ...prev, end_date: e.target.value }))
              }
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel>Meeting Duration</InputLabel>
              <Select
                value={dateRange.duration_minutes}
                label='Meeting Duration'
                onChange={e =>
                  setDateRange(prev => ({
                    ...prev,
                    duration_minutes: e.target.value as number,
                  }))
                }
              >
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
                <MenuItem value={90}>1.5 hours</MenuItem>
                <MenuItem value={120}>2 hours</MenuItem>
                <MenuItem value={180}>3 hours</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDateRangeDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setShowDateRangeDialog(false);
              fetchAzureCalendarSlots();
            }}
            variant='contained'
          >
            Get Slots
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SlotSelection;
